import React, { useState, useEffect } from 'react';
import { ChevronLeft, FileText, Download, Trash2, Plus, Euro, FileCheck } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const initialForm = {
  customerName: '',
  customerStreet: '',
  customerHouseNumber: '',
  customerPostalCode: '',
  customerCity: '',
  serviceDate: todayISO(),
  invoiceDate: todayISO(),
  timeSlot: '',
  rideType: 'sonstiges',
  transportType: 'animal',
  description: '',
  petName: '',
  petType: '',
  startAddress: '',
  endAddress: '',
  price: '',
  animalWelfareOrg: '',
  memberNumber: '',
  paymentStatus: 'paid',
  advancePayment: '',
  customText: '',
};

const AdminManualInvoices = ({ password, onBack, mode = 'invoice' }) => {
  const isQuote = mode === 'quote';
  const cfg = isQuote ? {
    endpoint: 'manual-quotes',
    numberField: 'quoteNumber',
    dateField: 'quoteDate',
    title: 'Kostenvoranschläge erstellen',
    subtitle: 'Manuelle Kostenvoranschläge mit fortlaufender Nummerierung',
    newButton: 'Neuer Kostenvoranschlag',
    dialogTitle: 'Neuen Kostenvoranschlag erstellen',
    dialogDescription: 'Die Angebotsnummer wird automatisch fortlaufend vergeben. Das PDF wird sofort heruntergeladen und im Archiv gespeichert.',
    numberHeaderLabel: 'Nummer',
    fileNamePrefix: 'Kostenvoranschlag',
    amountLabel: 'Angebotsbetrag (EUR) *',
    dateLabel: 'Angebotsdatum',
    dateHint: '(default: heute)',
    serviceDateLabel: 'Voraussichtliches Leistungsdatum *',
    submitLabel: 'Erstellen & Herunterladen',
    successLabel: 'Kostenvoranschlag',
    downloadIdHeader: 'x-quote-number',
    emptyLabel: 'Noch keine Kostenvoranschläge erstellt.',
    deleteConfirm: (item) => `Kostenvoranschlag ${item[cfg.numberField]} wirklich aus dem Archiv loeschen?`,
  } : {
    endpoint: 'manual-invoices',
    numberField: 'invoiceNumber',
    dateField: 'invoiceDate',
    title: 'Rechnungen erstellen',
    subtitle: 'Manuelle Rechnungen mit fortlaufender Nummerierung',
    newButton: 'Neue Rechnung',
    dialogTitle: 'Neue Rechnung erstellen',
    dialogDescription: 'Die Rechnungsnummer wird automatisch fortlaufend vergeben. Das PDF wird sofort heruntergeladen und im Archiv gespeichert.',
    numberHeaderLabel: 'Nummer',
    fileNamePrefix: 'Rechnung',
    amountLabel: 'Rechnungsbetrag (EUR) *',
    dateLabel: 'Rechnungsdatum',
    dateHint: '(auch rückwirkend möglich)',
    serviceDateLabel: 'Leistungsdatum *',
    submitLabel: 'Erstellen & Herunterladen',
    successLabel: 'Rechnung',
    downloadIdHeader: 'x-invoice-number',
    emptyLabel: 'Noch keine manuellen Rechnungen erstellt.',
    deleteConfirm: (item) => `Rechnung ${item[cfg.numberField]} wirklich aus dem Archiv loeschen?`,
  };
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/${cfg.endpoint}?password=${encodeURIComponent(password)}`);
      setInvoices(res.data || []);
    } catch (e) {
      toast.error(`Fehler beim Laden der ${cfg.successLabel === 'Rechnung' ? 'Rechnungen' : 'Kostenvoranschläge'}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadInvoices(); }, []);

  const updateField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = async () => {
    if (!form.customerName.trim()) { toast.error('Kundenname ist Pflicht'); return; }
    if (!form.serviceDate) { toast.error('Leistungsdatum fehlt'); return; }
    const priceNum = parseFloat(form.price.toString().replace(',', '.'));
    if (isNaN(priceNum) || priceNum < 0) { toast.error('Bitte gueltigen Betrag eingeben'); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: priceNum,
        animalWelfareOrg: form.animalWelfareOrg.trim() || null,
        memberNumber: form.memberNumber.trim() || null,
        advancePayment: form.paymentStatus === 'open' && form.advancePayment
          ? parseFloat(form.advancePayment.toString().replace(',', '.')) || null
          : null,
        customText: form.customText.trim() || null,
      };
      const res = await axios.post(
        `${API}/admin/${cfg.endpoint}?password=${encodeURIComponent(password)}`,
        payload,
        { responseType: 'blob' }
      );
      const invoiceNumber = res.headers[cfg.downloadIdHeader] || cfg.successLabel;
      downloadBlob(res.data, `${cfg.fileNamePrefix}_${invoiceNumber}.pdf`);
      toast.success(`${cfg.successLabel} ${invoiceNumber} erstellt und heruntergeladen`);
      setCreateOpen(false);
      setForm(initialForm);
      loadInvoices();
    } catch (e) {
      toast.error(`Fehler beim Erstellen (${cfg.successLabel})`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (inv) => {
    try {
      const res = await axios.get(
        `${API}/admin/${cfg.endpoint}/${inv.id}/download?password=${encodeURIComponent(password)}`,
        { responseType: 'blob' }
      );
      downloadBlob(res.data, `${cfg.fileNamePrefix}_${inv[cfg.numberField]}.pdf`);
    } catch (e) {
      toast.error('Download fehlgeschlagen');
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(cfg.deleteConfirm(inv) + `\n(Nummer bleibt im Counter erhalten)`)) return;
    try {
      await axios.delete(`${API}/admin/${cfg.endpoint}/${inv.id}?password=${encodeURIComponent(password)}`);
      toast.success(`${cfg.successLabel} geloescht`);
      loadInvoices();
    } catch (e) {
      toast.error('Loeschen fehlgeschlagen');
    }
  };

  const handleConvert = async (inv) => {
    if (!window.confirm(`Kostenvoranschlag ${inv.quoteNumber} in eine Rechnung umwandeln?\nEs wird eine neue Rechnung mit heutigem Rechnungsdatum erzeugt und heruntergeladen.`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/manual-quotes/${inv.id}/convert?password=${encodeURIComponent(password)}`,
        null,
        { responseType: 'blob' }
      );
      const invoiceNumber = res.headers['x-invoice-number'] || 'Rechnung';
      downloadBlob(res.data, `Rechnung_${invoiceNumber}.pdf`);
      toast.success(`Rechnung ${invoiceNumber} erstellt`);
      loadInvoices();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Umwandlung fehlgeschlagen';
      // Bei Blob-Response ist detail nicht direkt lesbar - fallback
      if (typeof msg === 'object') {
        toast.error('Umwandlung fehlgeschlagen');
      } else {
        toast.error(msg);
      }
    }
  };

  const fmtPrice = (p) => {
    const num = Number(p) || 0;
    return num.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="manual-invoices-back-btn">
          <ChevronLeft className="h-4 w-4 mr-2" /> Zurueck zum Admin
        </Button>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {cfg.title}
              </CardTitle>
              <CardDescription>{cfg.subtitle}</CardDescription>
            </div>
            <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="create-manual-invoice-btn">
              <Plus className="h-4 w-4 mr-2" /> {cfg.newButton}
            </Button>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center text-gray-500 py-8">Wird geladen...</div>
            ) : invoices.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                {cfg.emptyLabel}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="p-3">{cfg.numberHeaderLabel}</th>
                      <th className="p-3">Kunde</th>
                      <th className="p-3">Leistungsdatum</th>
                      <th className="p-3">Erstellt</th>
                      <th className="p-3 text-right">Betrag</th>
                      <th className="p-3 text-center">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="border-b hover:bg-gray-50" data-testid={`manual-invoice-row-${inv.id}`}>
                        <td className="p-3 font-mono text-xs">
                          {inv[cfg.numberField]}
                          {isQuote && inv.convertedToInvoice && (
                            <div className="mt-1 inline-flex items-center gap-1 bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded">
                              <FileCheck className="h-3 w-3" />
                              {inv.convertedToInvoice}
                            </div>
                          )}
                        </td>
                        <td className="p-3 font-medium">{inv.payload?.customerName}</td>
                        <td className="p-3 text-gray-600">{inv.payload?.serviceDate}</td>
                        <td className="p-3 text-gray-500 text-xs">{(inv.createdAt || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="p-3 text-right font-semibold text-blue-700">{fmtPrice(inv.payload?.price)}</td>
                        <td className="p-3 text-center">
                          {isQuote && !inv.convertedToInvoice && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleConvert(inv)}
                              title="In Rechnung umwandeln"
                              data-testid={`convert-quote-btn-${inv.id}`}
                            >
                              <FileCheck className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleDownload(inv)} title="PDF herunterladen" data-testid={`download-invoice-btn-${inv.id}`}>
                            <Download className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(inv)} title="Aus Archiv entfernen" data-testid={`delete-invoice-btn-${inv.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog: Neue Rechnung/Kostenvoranschlag */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-invoice-dialog">
          <DialogHeader>
            <DialogTitle>{cfg.dialogTitle}</DialogTitle>
            <DialogDescription>
              {cfg.dialogDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Transporttyp-Auswahl (nur Kostenvoranschläge) */}
            {isQuote && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="transport-type-section">
                <Label className="text-xs font-semibold text-blue-900">Art des Transports *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      updateField('transportType', 'animal');
                      // rideType passend zurücksetzen falls auf Sach-Wert
                      if (['umzug', 'moebel', 'sachen'].includes(form.rideType)) {
                        updateField('rideType', 'sonstiges');
                      }
                    }}
                    className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-colors ${
                      form.transportType === 'animal'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                    }`}
                    data-testid="transport-type-animal-btn"
                  >
                    🐾 Tiertransport
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      updateField('transportType', 'goods');
                      // Tier-Felder leeren + rideType auf Umzug setzen falls auf Tier-Wert
                      updateField('petName', '');
                      updateField('petType', '');
                      updateField('animalWelfareOrg', '');
                      if (['tierarzt', 'tierklinik', 'sonstiges'].includes(form.rideType)) {
                        updateField('rideType', 'umzug');
                      }
                    }}
                    className={`px-3 py-2 rounded-md text-sm font-medium border-2 transition-colors ${
                      form.transportType === 'goods'
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                    }`}
                    data-testid="transport-type-goods-btn"
                  >
                    📦 Sachtransport / Umzug
                  </button>
                </div>
              </div>
            )}

            {/* Kunde */}
            <div className="space-y-2">
              <Label className="font-semibold">Kunde *</Label>
              <Input
                placeholder="Vor- und Nachname (oder Firma)"
                value={form.customerName}
                onChange={(e) => updateField('customerName', e.target.value)}
                data-testid="invoice-customer-name-input"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input className="md:col-span-2" placeholder="Strasse" value={form.customerStreet} onChange={(e) => updateField('customerStreet', e.target.value)} />
                <Input placeholder="Hausnr." value={form.customerHouseNumber} onChange={(e) => updateField('customerHouseNumber', e.target.value)} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="PLZ" value={form.customerPostalCode} onChange={(e) => updateField('customerPostalCode', e.target.value)} />
                <Input className="col-span-2" placeholder="Ort" value={form.customerCity} onChange={(e) => updateField('customerCity', e.target.value)} />
              </div>
            </div>

            {/* Leistung */}
            <div className="space-y-2 border-t pt-4">
              <Label className="font-semibold">Leistung</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{cfg.serviceDateLabel}</Label>
                  <Input type="date" value={form.serviceDate} onChange={(e) => updateField('serviceDate', e.target.value)} data-testid="invoice-service-date-input" />
                </div>
                <div>
                  <Label className="text-xs">Uhrzeit</Label>
                  <Input placeholder="z.B. 10:00" value={form.timeSlot} onChange={(e) => updateField('timeSlot', e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-2">
                  {cfg.dateLabel}
                  <span className="text-[10px] font-normal text-gray-500">{cfg.dateHint}</span>
                </Label>
                <Input
                  type="date"
                  value={form.invoiceDate}
                  onChange={(e) => updateField('invoiceDate', e.target.value)}
                  data-testid="invoice-invoice-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Fahrtart</Label>
                <Select value={form.rideType} onValueChange={(v) => updateField('rideType', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isQuote && form.transportType === 'goods' ? (
                      <>
                        <SelectItem value="umzug">Umzug</SelectItem>
                        <SelectItem value="moebel">Möbeltransport</SelectItem>
                        <SelectItem value="sachen">Sonstiger Sachtransport</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="tierarzt">Tierarztfahrt</SelectItem>
                        <SelectItem value="tierklinik">Tierklinikfahrt</SelectItem>
                        <SelectItem value="sonstiges">Sonstiger Tiertransport</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {!(isQuote && form.transportType === 'goods') && (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Tiername" value={form.petName} onChange={(e) => updateField('petName', e.target.value)} data-testid="invoice-pet-name-input" />
                  <Input placeholder="Tierart (Hund, Katze...)" value={form.petType} onChange={(e) => updateField('petType', e.target.value)} data-testid="invoice-pet-type-input" />
                </div>
              )}
              <Input placeholder="Abholadresse" value={form.startAddress} onChange={(e) => updateField('startAddress', e.target.value)} />
              <Input placeholder="Zieladresse" value={form.endAddress} onChange={(e) => updateField('endAddress', e.target.value)} />
              <Textarea
                placeholder={isQuote && form.transportType === 'goods'
                  ? 'Zu transportierende Gegenstände (Möbel, Kartons, Geräte...)'
                  : 'Optionale zusaetzliche Beschreibung (Notiz, weitere Details)'}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={2}
                data-testid="invoice-description-input"
              />
            </div>

            {/* Optionale Felder */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border-t pt-4">
              {!(isQuote && form.transportType === 'goods') && (
                <div>
                  <Label className="text-xs">Tierschutzverein (optional)</Label>
                  <Input placeholder="z.B. Tierheim Hagen e.V." value={form.animalWelfareOrg} onChange={(e) => updateField('animalWelfareOrg', e.target.value)} />
                </div>
              )}
              <div>
                <Label className="text-xs">Mitgliedsnummer (optional)</Label>
                <Input placeholder="z.B. 000012" value={form.memberNumber} onChange={(e) => updateField('memberNumber', e.target.value)} />
              </div>
            </div>

            {/* Betrag */}
            <div className="border-t pt-4">
              <Label className="font-semibold flex items-center gap-2">
                <Euro className="h-4 w-4 text-blue-600" /> {cfg.amountLabel}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="z.B. 35.00"
                value={form.price}
                onChange={(e) => updateField('price', e.target.value)}
                className="text-lg font-bold"
                data-testid="invoice-price-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Hinweis Kleinunternehmerregelung &sect; 19 UStG: keine USt-Ausweisung.
              </p>
            </div>

            {/* Zahlungsstatus - nur bei Rechnungen */}
            {!isQuote && (
              <div className="border-t pt-4 space-y-3">
              <Label className="font-semibold">Zahlungsstatus</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => updateField('paymentStatus', 'paid')}
                  className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                    form.paymentStatus === 'paid'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="invoice-payment-paid-btn"
                >
                  Zahlung bereits erfolgt
                </button>
                <button
                  type="button"
                  onClick={() => updateField('paymentStatus', 'open')}
                  className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                    form.paymentStatus === 'open'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="invoice-payment-open-btn"
                >
                  Zahlung offen
                </button>
              </div>

              {form.paymentStatus === 'open' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                  <Label className="text-xs">Anzahlung faellig (optional, EUR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="z.B. 20.00 - leer lassen fuer keine Anzahlung"
                    value={form.advancePayment}
                    onChange={(e) => updateField('advancePayment', e.target.value)}
                    data-testid="invoice-advance-payment-input"
                  />
                  <p className="text-xs text-gray-600">
                    Wenn eine Anzahlung angegeben wird, erscheint sie auf der Rechnung zusammen mit dem Restbetrag.
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Zusatztext */}
            <div className="border-t pt-4 space-y-2">
              <Label className="font-semibold">Zusatztext auf dem PDF (optional)</Label>
              <Textarea
                placeholder={isQuote
                  ? 'Freier Text unten auf dem Kostenvoranschlag. Z.B. Hinweise zu Zusatzleistungen oder persönlicher Gruß.'
                  : 'Freier Text, der als Hinweis-Block unten auf die Rechnung gedruckt wird. Z.B. Zahlungsziel, Gutscheincode, persoenlicher Gruss.'}
                value={form.customText}
                onChange={(e) => updateField('customText', e.target.value)}
                rows={3}
                data-testid="invoice-custom-text-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={submitting}>Abbrechen</Button>
            <Button onClick={handleCreate} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-manual-invoice-btn">
              <Download className="h-4 w-4 mr-2" />
              {submitting ? 'Wird erstellt...' : cfg.submitLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManualInvoices;
